import { ThemeProvider } from '@mui/material';
import { LoginCallback, Security } from '@okta/okta-react';
import { oktaAuth } from './services/oktaAuth';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { Route, Routes, useNavigate } from 'react-router';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { GuardedRoute } from './components/GuardedRoute/GuardedRoute';
import { ROUTES } from './constants/routes';
import { AllTimesheets } from './pages/AllTimesheets/AllTimesheets';
import { Deals } from './pages/Deals/Deals';
import { DefaultPageRedirect } from './pages/DefaultPageRedirect/DefaultPageRedirect';
import { IssuedTimesheets } from './pages/IssuedTimesheets/IssuedTimesheets';
import { PendingTimesheetsMonthDetail } from './pages/IssuedTimesheets/PendingTimesheetsMonthDetail';
import { ProxyAssignment } from './pages/ProxyAssignment/ProxyAssignment';
import SingleTimesheet from './pages/SingleTimesheet/SingleTimesheet';
import { TimesheetAssignment } from './pages/TimesheetAssignment/TimesheetAssignment';
import { UserAdministration } from './pages/UserAdministration/UserAdministration';
import { Layout } from './pages/layout/Layout';
import { selectedThemeState } from './state/UIState';
import { darkTheme } from './themes/dark-theme/dark-theme';
import { lightTheme } from './themes/light-theme/light-theme';
import { THEME } from './types';
import { OpManagerSubmittedTimesheets } from './pages/OpManagerSubmittedTimesheets/OpManagerSubmittedTimesheets';
import { OpManagerAllTimesheets } from './pages/OpManagerAllTimesheets/OpManagerAllTimesheets';
import { OpManagerSingleTimesheet } from './pages/OpManagerSingleTimesheet/OpManagerSingleTimesheet';
import DealOwnerDeals from './pages/DealOwnerDeals/DealOwnerDeals';
import DealOwnerAllTimesheets from './pages/DealOwnerAllTimesheets/DealOwnerAllTimesheets';
import DealOwnerPendingTimesheets from './pages/DealOwnerPendingTimesheets/DealOwnerPendingTimesheets';
import { DealOwnerSingleTimesheet } from './pages/DealOwnerSingleTimesheet/DealOwnerSingleTimesheet';
import { OpManagerOperatingPartners } from './pages/OpManagerOperatingPartners/OpManagerOperatingPartners';
import { AccountantTimesheetStatuses } from './pages/AccountantTimesheetStatuses/AccountantTimesheetStatuses';
import { AccountantOpOverview } from './pages/AccountantOpOverview/AccountantOpOverview';
import { AccountantSingleTimesheet } from './pages/AccountantSingleTimesheet/AccountantSingleTimesheet';
import { HolidaySchedule } from './pages/HolidaySchedule/HolidaySchedule';
import { SingleTimesheetSummary } from './pages/TimesheetsSummary/SingleTimesheetSummary';
import { OPTimesheetsSummary } from './pages/TimesheetsSummary/OPTimesheetsSummary';
import { ProxyTimesheetsSummary } from './pages/TimesheetsSummary/ProxyTimesheetSummary';
import { OpManagerTimesheetsSummary } from './pages/OpManagerTimesheetsSummary/OpManagerTimesheetsSummary';
import DealOwnerOpChargeSummary from './pages/DealOwnerOpChargeSummary/DealOwnerOpChargeSummary';
import { TimesheetApprovals } from './pages/TimesheetApprovals/TimesheetApprovals';
import { Workflows } from './pages/Workflows/Workflows';
import { ApprovalDelegates } from './pages/ApprovalDelegates/ApprovalDelegates';

function App() {
  const [selectedTheme, setSelectedTheme] = useAtom(selectedThemeState);
  const navigate = useNavigate();

  const restoreOriginalUri = () => {
    navigate(`/`);
  };

  const theme = useMemo(() => {
    if (selectedTheme) {
      return selectedTheme;
    }
    const localStorageTheme = localStorage.getItem('theme') as THEME | undefined;
    if (!localStorageTheme) {
      localStorage.setItem('theme', THEME.DARK);
      setSelectedTheme(THEME.DARK);
      return THEME.DARK;
    }
    setSelectedTheme(localStorageTheme);
    return localStorageTheme;
  }, [selectedTheme, setSelectedTheme]);

  const themeObj = useMemo(() => {
    if (theme === THEME.DARK) {
      return darkTheme;
    }
    return lightTheme;
  }, [theme]);

  return (
    <ThemeProvider theme={themeObj}>
      <ErrorBoundary>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <Routes>
          <Route path={'/login/callback'} element={<LoginCallback />} />
          <Route path={'/'} element={<Layout />}>
            <Route index element={<DefaultPageRedirect />} />
            <Route
              path={`/${ROUTES.TIMESHEETS_ASSIGNMENT}`}
              element={
                <GuardedRoute
                  path={ROUTES.TIMESHEETS_ASSIGNMENT}
                  component={<TimesheetAssignment />}
                />
              }
            />
            <Route
              path={`/${ROUTES.DEALS}`}
              element={<GuardedRoute path={ROUTES.DEALS} component={<Deals />} />}
            />
            <Route
              path={`/${ROUTES.PROXY_ASSIGNMENT}`}
              element={
                <GuardedRoute path={ROUTES.PROXY_ASSIGNMENT} component={<ProxyAssignment />} />
              }
            />
            <Route
              path={`/${ROUTES.USER_ADMINISTRATION}`}
              element={
                <GuardedRoute
                  path={ROUTES.USER_ADMINISTRATION}
                  component={<UserAdministration />}
                />
              }
            />
            <Route
              path={`/${ROUTES.EVENT_SCHEDULE}`}
              element={
                <GuardedRoute path={ROUTES.EVENT_SCHEDULE} component={<HolidaySchedule />} />
              }
            />
            <Route
              path={`/${ROUTES.WORKFLOWS}`}
              element={<GuardedRoute path={ROUTES.WORKFLOWS} component={<Workflows />} />}
            />
            <Route
              path={`/${ROUTES.APPROVAL_DELEGATES}`}
              element={
                <GuardedRoute path={ROUTES.APPROVAL_DELEGATES} component={<ApprovalDelegates />} />
              }
            />
            <Route
              path={`/${ROUTES.TIMESHEET_APPROVALS}`}
              element={
                <GuardedRoute path={ROUTES.TIMESHEET_APPROVALS} component={<TimesheetApprovals />} />
              }
            />
            <Route
              path={`/${ROUTES.PENDING_TIMESHEETS}`}
              element={
                <GuardedRoute path={ROUTES.PENDING_TIMESHEETS} component={<IssuedTimesheets />} />
              }
            />
            <Route
              path={`/${ROUTES.ISSUED_TIMESHEETS}`}
              element={
                <GuardedRoute path={ROUTES.ISSUED_TIMESHEETS} component={<IssuedTimesheets />} />
              }
            />
            <Route
              path={`/${ROUTES.ALL_TIMESHEETS}`}
              element={<GuardedRoute path={ROUTES.ALL_TIMESHEETS} component={<AllTimesheets />} />}
            />
            <Route
              path={`/${ROUTES.PROXY_ALL_TIMESHEETS}`}
              element={
                <GuardedRoute path={ROUTES.PROXY_ALL_TIMESHEETS} component={<AllTimesheets />} />
              }
            />
            <Route
              path={`/${ROUTES.PENDING_TIMESHEETS}/month/:year/:month`}
              element={
                <GuardedRoute
                  path={ROUTES.PENDING_TIMESHEETS}
                  component={<PendingTimesheetsMonthDetail />}
                />
              }
            />
            <Route
              path={`/${ROUTES.PENDING_TIMESHEETS}/:id`}
              element={
                <GuardedRoute path={ROUTES.PENDING_TIMESHEETS} component={<SingleTimesheet />} />
              }
            />
            <Route
              path={`/${ROUTES.ISSUED_TIMESHEETS}/:id`}
              element={
                <GuardedRoute path={ROUTES.ISSUED_TIMESHEETS} component={<SingleTimesheet />} />
              }
            />
            <Route
              path={`/${ROUTES.ALL_TIMESHEETS}/:id`}
              element={
                <GuardedRoute path={ROUTES.ALL_TIMESHEETS} component={<SingleTimesheet />} />
              }
            />
            <Route
              path={`/${ROUTES.TIMESHEETS_SUMMARY}`}
              element={
                <GuardedRoute
                  path={ROUTES.TIMESHEETS_SUMMARY}
                  component={<OPTimesheetsSummary />}
                />
              }
            />
            <Route
              path={`/${ROUTES.TIMESHEETS_SUMMARY}/:id`}
              element={
                <GuardedRoute
                  path={ROUTES.TIMESHEETS_SUMMARY}
                  component={<SingleTimesheetSummary />}
                />
              }
            />
            <Route
              path={`/${ROUTES.PROXY_TIMESHEETS_SUMMARY}`}
              element={
                <GuardedRoute
                  path={ROUTES.PROXY_TIMESHEETS_SUMMARY}
                  component={<ProxyTimesheetsSummary />}
                />
              }
            />
            <Route
              path={`/${ROUTES.PROXY_TIMESHEETS_SUMMARY}/:id`}
              element={
                <GuardedRoute
                  path={ROUTES.PROXY_TIMESHEETS_SUMMARY}
                  component={<SingleTimesheetSummary />}
                />
              }
            />
            {/* //OP Manager */}
            <Route
              path={`/${ROUTES.OP_MANAGER_ALL_TIMESHEETS}`}
              element={
                <GuardedRoute
                  path={ROUTES.OP_MANAGER_ALL_TIMESHEETS}
                  component={<OpManagerAllTimesheets />}
                />
              }
            />
            <Route
              path={`/${ROUTES.OP_MANAGER_SUBMITTED_TIMESHEET}`}
              element={
                <GuardedRoute
                  path={ROUTES.OP_MANAGER_SUBMITTED_TIMESHEET}
                  component={<OpManagerSubmittedTimesheets />}
                />
              }
            />
            <Route
              path={`/${ROUTES.OP_MANAGER_ALL_OPERATING_PARTNERS}`}
              element={
                <GuardedRoute
                  path={ROUTES.OP_MANAGER_ALL_OPERATING_PARTNERS}
                  component={<OpManagerOperatingPartners />}
                />
              }
            />
            <Route
              path={`/${ROUTES.OP_MANAGER_ALL_TIMESHEETS}/:id`}
              element={
                <GuardedRoute
                  path={ROUTES.OP_MANAGER_ALL_TIMESHEETS}
                  component={<OpManagerSingleTimesheet />}
                />
              }
            />
            <Route
              path={`/${ROUTES.OP_MANAGER_SUBMITTED_TIMESHEET}/:id`}
              element={
                <GuardedRoute
                  path={ROUTES.OP_MANAGER_SUBMITTED_TIMESHEET}
                  component={<OpManagerSingleTimesheet />}
                />
              }
            />
            <Route
              path={`/${ROUTES.OP_MANAGER_TIMESHEETS_SUMMARY}`}
              element={
                <GuardedRoute
                  path={ROUTES.OP_MANAGER_TIMESHEETS_SUMMARY}
                  component={<OpManagerTimesheetsSummary />}
                />
              }
            />

            {/*  */}
            {/* //Deal owner*/}
            <Route
              path={`/${ROUTES.DEAL_OWNER_DEALS}`}
              element={
                <GuardedRoute path={ROUTES.DEAL_OWNER_DEALS} component={<DealOwnerDeals />} />
              }
            />
            <Route
              path={`/${ROUTES.DEAL_OWNER_ALL_TIMESHEETS}`}
              element={
                <GuardedRoute
                  path={ROUTES.DEAL_OWNER_ALL_TIMESHEETS}
                  component={<DealOwnerAllTimesheets />}
                />
              }
            />
            <Route
              path={`/${ROUTES.DEAL_OWNER_PENDING_TIMESHEETS}`}
              element={
                <GuardedRoute
                  path={ROUTES.DEAL_OWNER_PENDING_TIMESHEETS}
                  component={<DealOwnerPendingTimesheets />}
                />
              }
            />
            <Route
              path={`/${ROUTES.DEAL_OWNER_OP_CHARGE_SUMMARY}`}
              element={
                <GuardedRoute
                  path={ROUTES.DEAL_OWNER_OP_CHARGE_SUMMARY}
                  component={<DealOwnerOpChargeSummary />}
                />
              }
            />
            <Route
              path={`/${ROUTES.DEAL_OWNER_PENDING_TIMESHEETS}/:id`}
              element={
                <GuardedRoute
                  path={ROUTES.DEAL_OWNER_PENDING_TIMESHEETS}
                  component={<DealOwnerSingleTimesheet />}
                />
              }
            />
            <Route
              path={`/${ROUTES.DEAL_OWNER_ALL_TIMESHEETS}/:id`}
              element={
                <GuardedRoute
                  path={ROUTES.DEAL_OWNER_ALL_TIMESHEETS}
                  component={<DealOwnerSingleTimesheet />}
                />
              }
            />
            {/* //Accountant*/}
            <Route
              path={`/${ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES}`}
              element={
                <GuardedRoute
                  path={ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES}
                  component={<AccountantTimesheetStatuses />}
                />
              }
            />
            <Route
              path={`/${ROUTES.ACCOUNTANT_OP_OVERVIEW}`}
              element={
                <GuardedRoute
                  path={ROUTES.ACCOUNTANT_OP_OVERVIEW}
                  component={<AccountantOpOverview />}
                />
              }
            />
            <Route
              path={`/${ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES}/:id`}
              element={
                <GuardedRoute
                  path={ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES}
                  component={<AccountantSingleTimesheet />}
                />
              }
            />
          </Route>
        </Routes>
      </Security>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
